import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageShell } from '@/components/layout/PageShell';
import { SectionMarker } from '@/components/brand/SectionMarker';
import { StatusDot } from '@/components/ui/StatusDot';
import { Icon } from '@/components/ui/Icon';
import { NeonButton } from '@/components/ui/NeonButton';
import { Sheet } from '@/components/sheets/Sheet';
import { ActionSheet, type ActionSheetItem } from '@/components/sheets/ActionSheet';
import { ROUTES, LIVE_SURFACES } from '@/config/routes';
import { cn } from '@/lib/cn';
import { useAuth } from '@/modules/auth/useAuth';
import { MfaEnrolSheet } from '@/modules/auth/MfaEnrolSheet';
import { useIsModerator } from '@/modules/admin/useIsModerator';
import { useBlocks } from '@/modules/trust/useBlocks';
import { usePushSubscription } from '@/modules/push/usePushSubscription';
import { useCheckout } from '@/modules/billing/useCheckout';
import { usePlan } from '@/modules/billing/usePlan';
import { useTravelMode } from '@/modules/travel/useTravelMode';
import { TravelSheet } from '@/modules/travel/TravelSheet';
import { hasPersona, hasStripe } from '@/config/env';

interface ToggleRow {
  id: string;
  label: string;
  sub: string;
  on: boolean;
}

/**
 * Settings — restrained editorial list. Not a dashboard.
 * Toggles are local state — flips persist within the session only.
 *
 * Account section dispatches sheets:
 *   - Blocked profiles → ActionSheet with stub blocked handles + unblock per row
 *   - Delete account → confirmation Sheet with destructive CTA (no real delete)
 */
export const SettingsPage = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<ToggleRow[]>([
    { id: 'presence', label: 'Show me as live', sub: 'Pink dot on your profile when in app.', on: true },
    { id: 'distance', label: 'Share approximate distance', sub: 'Coarse only. Never an address.', on: true },
    { id: 'pulse', label: 'Surface in pulse rooms', sub: 'You appear higher in active rooms.', on: false },
    { id: 'ghost', label: 'Quiet mode', sub: 'You see, you are not seen.', on: false },
  ]);

  const [blockedOpen, setBlockedOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [mfaOpen, setMfaOpen] = useState(false);
  const [travelOpen, setTravelOpen] = useState(false);
  const [mfaEnrolled, setMfaEnrolled] = useState(false);

  const { logOut, listFactors } = useAuth();
  const { isModerator } = useIsModerator();
  const { blockedIds, unblock } = useBlocks();
  const push = usePushSubscription();
  const { openPortal } = useCheckout();
  const { plan } = usePlan();
  const { travel } = useTravelMode();

  useEffect(() => {
    (async () => {
      const factors = await listFactors();
      setMfaEnrolled(factors.some((f) => f.status === 'verified'));
    })();
  }, [listFactors, mfaOpen]);

  const toggle = (id: string) =>
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, on: !r.on } : r)));

  const blockedArr = Array.from(blockedIds);
  const blockedItems: ActionSheetItem[] = blockedArr.length
    ? blockedArr.map((id) => ({
        id,
        label: id.slice(0, 8),
        sub: 'Tap to unblock.',
        icon: 'block',
        onSelect: () => { void unblock(id); },
      }))
    : [
        {
          id: 'empty',
          label: 'Nobody blocked',
          sub: "You're clear.",
          icon: 'check',
          onSelect: () => {},
        },
      ];

  return (
    <PageShell
      rail={false}
      mobileTopBar={
        <>
          {/* Identity is the WorldSwitcher in PageShell's bar — one lockup.
              This spacer keeps the page label on the right edge. */}
          <span aria-hidden />
          <span className="font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
            Settings
          </span>
        </>
      }
    >
      {/* HEADER */}
      <section className="border-b border-white/[0.06] px-5 py-7 md:px-12 md:py-12">
        <SectionMarker className="mb-3">Settings · the rules of the room</SectionMarker>
        <h1
          className="font-display font-black uppercase tracking-display"
          style={{
            fontSize: 'clamp(34px, 5vw, 80px)',
            lineHeight: 0.9,
            letterSpacing: '-0.04em',
          }}
        >
          Your vibe<span className="text-pink">.</span>
          <br />
          <span className="font-light italic text-text-secondary">Your rules</span>
          <span className="text-pink">.</span>
        </h1>
      </section>

      <div className="mx-auto max-w-3xl space-y-12 px-5 py-10 md:px-12 md:py-14">
        <Section title="Presence">
          <ul className="divide-y divide-white/[0.07] border-y border-white/[0.07]">
            {rows.map((r) => (
              <li key={r.id}>
                <button
                  onClick={() => toggle(r.id)}
                  className="flex w-full items-center justify-between gap-6 py-4 text-left"
                >
                  <div className="min-w-0">
                    <p className="font-display text-sm font-bold uppercase tracking-wide text-text-primary">
                      {r.label}
                    </p>
                    <p className="mt-1 text-xs text-text-secondary">{r.sub}</p>
                  </div>
                  <span
                    aria-hidden
                    className={cn(
                      'relative h-6 w-11 shrink-0 border transition-colors duration-fast ease-signal',
                      r.on
                        ? 'border-pink bg-pink/10'
                        : 'border-white/15 bg-charcoal',
                    )}
                  >
                    <span
                      className={cn(
                        'absolute top-0.5 h-4 w-4 transition-all duration-base ease-signal',
                        r.on ? 'left-6 bg-pink shadow-glow-soft' : 'left-0.5 bg-white/60',
                      )}
                    />
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </Section>

        <Section title="Surfaces">
          <ul className="divide-y divide-white/[0.07] border-y border-white/[0.07]">
            <Row icon="apps" label="HOTTR.app" sub="This surface. Live." status="live" />
            <Row
              icon="fingerprint"
              label="HOTTR.world"
              sub="Identity layer. One profile, every version of you."
              onClick={() => navigate(ROUTES.world)}
            />
            {/* .vip is off until its backend ships — the same gate as its
                routes in AppRoutes. Nothing is offered that has nowhere to go. */}
            {LIVE_SURFACES.vip && (
              <Row
                icon="diamond"
                label="HOTTR.vip"
                sub="Directory preview. Discovery only."
                onClick={() => navigate(ROUTES.vip.roster)}
              />
            )}
          </ul>
        </Section>

        <Section title="Account">
          <ul className="divide-y divide-white/[0.07] border-y border-white/[0.07]">
            <Row icon="badge" label="HOTTR ID" sub="Pseudonymous. Never a phone or email." />
            <Row icon="alternate_email" label="Handle" sub="Change once per 30 days." />
            <Row
              icon="security"
              label="Two-factor authentication"
              sub={mfaEnrolled ? 'Active — TOTP authenticator.' : 'Not yet enrolled.'}
              meta={mfaEnrolled ? 'on' : 'off'}
              onClick={() => setMfaOpen(true)}
            />
            {hasPersona() && (
              <Row
                icon="verified"
                label="Verify identity"
                sub="Persona ID + selfie. Handled quietly."
                onClick={() => navigate(ROUTES.verify)}
              />
            )}
            {hasStripe() && (
              <>
                <Row
                  icon="workspace_premium"
                  label={`Plan: ${plan}`}
                  sub={plan === 'free' ? 'Free — upgrade for travel + advanced filters.' : 'Manage subscription.'}
                  onClick={() => navigate(ROUTES.plans)}
                />
                {plan !== 'free' && (
                  <Row
                    icon="credit_card"
                    label="Billing portal"
                    sub="Update card, download invoices, cancel."
                    onClick={() => { void openPortal(); }}
                  />
                )}
              </>
            )}
            <Row
              icon="explore"
              label="Travel mode"
              sub={travel ? `${travel.city.label} · until ${new Date(travel.until).toLocaleDateString()}` : 'Set the room you’re in tonight.'}
              meta={travel?.city.label}
              onClick={() => setTravelOpen(true)}
            />
            <Row
              icon="logout"
              label="Sign out"
              sub="End the session on this device."
              onClick={() => { void logOut(); navigate(ROUTES.landing); }}
            />
            {isModerator && (
              <Row
                icon="admin_panel_settings"
                label="Open admin"
                sub="Moderation queue, safety, revenue, launch gates."
                onClick={() => navigate(ROUTES.admin.queue)}
              />
            )}
          </ul>
        </Section>

        <Section title="Notifications">
          <ul className="divide-y divide-white/[0.07] border-y border-white/[0.07]">
            {push.available ? (
              <Row
                icon="notifications_active"
                label={push.subscribed ? 'Push notifications · on' : 'Push notifications · off'}
                sub={
                  push.permission === 'denied'
                    ? 'Blocked at the browser level. Enable in site settings.'
                    : push.subscribed
                      ? 'Messages and Hottie nudges arrive even when HOTTR is closed.'
                      : 'Tap to enable.'
                }
                meta={push.subscribed ? 'on' : 'off'}
                onClick={() => {
                  if (push.busy) return;
                  void (push.subscribed ? push.unsubscribe() : push.subscribe());
                }}
              />
            ) : (
              <Row
                icon="notifications_off"
                label="Push notifications"
                sub="Not available in this browser."
                meta="—"
              />
            )}
          </ul>
        </Section>

        <Section title="Safety + privacy">
          <ul className="divide-y divide-white/[0.07] border-y border-white/[0.07]">
            <Row
              icon="block"
              label="Blocked profiles"
              sub={
                blockedArr.length === 0
                  ? "You're clear. No blocks."
                  : `${blockedArr.length} blocked.`
              }
              meta={blockedArr.length > 0 ? String(blockedArr.length) : undefined}
              onClick={() => setBlockedOpen(true)}
            />
            <Row
              icon="delete_forever"
              label="Delete account"
              sub="Permanent. Profile, messages, history."
              destructive
              onClick={() => setDeleteOpen(true)}
            />
          </ul>
        </Section>

        <p className="text-center font-display text-[9px] font-bold uppercase tracking-widest text-text-muted">
          Build {new Date().getFullYear()} — HOTTR<span className="text-pink">.</span>app prototype<span className="text-pink">.</span>
        </p>
      </div>

      {/* Blocked profiles sheet */}
      <ActionSheet
        open={blockedOpen}
        onClose={() => setBlockedOpen(false)}
        eyebrow="Safety · blocked"
        title={
          <>
            Blocked profiles<span className="text-pink">.</span>
          </>
        }
        message={
          blockedArr.length === 0
            ? "You're clear. Nobody is blocked."
            : 'Tap a handle to unblock. They will not be notified.'
        }
        items={blockedItems}
      />

      <MfaEnrolSheet open={mfaOpen} onClose={() => setMfaOpen(false)} onEnrolled={() => setMfaEnrolled(true)} />
      <TravelSheet open={travelOpen} onClose={() => setTravelOpen(false)} />

      {/* Delete account confirmation sheet */}
      <Sheet
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        eyebrow="Permanent"
        title={
          <>
            Delete account<span className="text-pink">.</span>
          </>
        }
        footer={
          <div className="flex items-center justify-end gap-3">
            <NeonButton variant="outline" size="md" onClick={() => setDeleteOpen(false)}>
              Cancel
            </NeonButton>
            <NeonButton
              variant="outline"
              size="md"
              className="border-pink text-pink hover:bg-pink/10"
              onClick={() => setDeleteOpen(false)}
            >
              Delete
            </NeonButton>
          </div>
        }
      >
        <p className="text-sm leading-relaxed text-text-secondary md:text-base">
          Your profile, messages, and history will be permanently removed
          <span className="text-pink">.</span> This cannot be undone
          <span className="text-pink">.</span>
        </p>
        <ul className="mt-5 space-y-2 font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
          <li>
            <span className="mr-2 text-pink">×</span>Profile and photos
          </li>
          <li>
            <span className="mr-2 text-pink">×</span>All messages and threads
          </li>
          <li>
            <span className="mr-2 text-pink">×</span>HEAT signals and history
          </li>
          <li>
            <span className="mr-2 text-pink">×</span>HOTTR ID
          </li>
        </ul>
        <p className="mt-6 font-display text-[10px] font-bold uppercase italic tracking-widest text-text-muted">
          You can sign back in within 30 days to restore<span className="text-pink">.</span>
        </p>
      </Sheet>
    </PageShell>
  );
};

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <section>
    <SectionMarker className="mb-4">{title}</SectionMarker>
    {children}
  </section>
);

const Row = ({
  icon,
  label,
  sub,
  status,
  meta,
  onClick,
  destructive = false,
}: {
  icon: string;
  label: string;
  sub: string;
  status?: 'live' | 'idle' | 'recent' | 'off';
  meta?: string;
  onClick?: () => void;
  destructive?: boolean;
}) => {
  const interactive = Boolean(onClick);
  const Inner = (
    <>
      <span
        className={cn(
          'flex h-10 w-10 shrink-0 items-center justify-center border bg-charcoal/60',
          destructive
            ? 'border-pink/40 text-pink'
            : 'border-white/[0.07] text-text-secondary',
        )}
      >
        <Icon name={icon} size={18} />
      </span>
      <div className="min-w-0 flex-1">
        <p
          className={cn(
            'font-display text-sm font-bold uppercase tracking-wide',
            destructive ? 'text-pink' : 'text-text-primary',
          )}
        >
          {label.replace('.', '')}
          {label.includes('.') && <span className="text-pink">.</span>}
          <span className="font-light italic text-text-secondary">
            {label.split('.')[1]}
          </span>
        </p>
        <p className="mt-1 truncate text-xs text-text-secondary">{sub}</p>
      </div>
      {meta && (
        <span className="font-display text-[10px] font-bold uppercase tracking-widest text-pink">
          {meta}
        </span>
      )}
      {interactive && (
        <Icon name="chevron_right" size={18} className="text-text-muted" />
      )}
      {!interactive && status && <StatusDot status={status} size="h-1.5 w-1.5" />}
    </>
  );

  return (
    <li className="contents">
      {interactive ? (
        <button
          type="button"
          onClick={onClick}
          className="flex w-full items-center gap-4 py-4 text-left transition-colors duration-fast ease-signal hover:bg-white/[0.02]"
        >
          {Inner}
        </button>
      ) : (
        <div className="flex items-center gap-4 py-4">{Inner}</div>
      )}
    </li>
  );
};
